import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SoftWallModal from './SoftWallModal';

const SoftwallContext = createContext(null);

const DEFAULT_TITLE = 'Support artists you love on HYK';
const LEGACY_GENERIC_COPY = /^sign up or log in to/i;

function normalizeTitle(value) {
  if (!value) return DEFAULT_TITLE;
  if (LEGACY_GENERIC_COPY.test(value.trim())) return DEFAULT_TITLE;
  return value;
}

export function SoftwallProvider({ children }) {
  const session = useSelector((s) => s.auth?.session);
  const isAuthed = Boolean(session);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const code = searchParams.get('code');
  const [views, setViews] = useState(0);
  const [interactions, setInteractions] = useState(0);
  const [maxScrollPct] = useState(0);
  const [locked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [initialMode, setInitialMode] = useState('signup');
  const [persistOnAuth, setPersistOnAuth] = useState(false);

  useEffect(() => {
    if (isAuthed && !persistOnAuth) {
      setIsOpen(false);
    }
  }, [isAuthed, persistOnAuth]);

  useEffect(() => {
    if (!token) return;
    setTitle('Create a password');
    setInitialMode('complete-signup');
    setPersistOnAuth(true);
    setIsOpen(true);
  }, [token]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setPersistOnAuth(false);
  }, []);

  const api = useMemo(
    () => ({
      isAuthed,
      locked,
      counters: { views, interactions, maxScrollPct },

      recordView: () => {
        if (isAuthed) return;
        setViews((v) => v + 1);
      },

      requireAuth: (ACTION_NAME = 'interaction', modalTitle = DEFAULT_TITLE) => {
        if (isAuthed) return true;

        void ACTION_NAME;
        setInteractions((i) => i + 1);

        setTitle(normalizeTitle(modalTitle));
        setInitialMode('signup');
        setPersistOnAuth(false);
        setIsOpen(true);

        return false;
      },

      openModal: (mode = 'signup') => {
        setInitialMode(mode);
        setPersistOnAuth(false);
        setIsOpen(true);
      },
    }),
    [isAuthed, locked, views, interactions, maxScrollPct]
  );
  return (
    <SoftwallContext.Provider value={api}>
      {children}

      {(isOpen || !isAuthed) && (
        <SoftWallModal
          open={isOpen}
          onClose={closeModal}
          onKeepOpenAfterAuthChange={setPersistOnAuth}
          title={title}
          initialMode={initialMode}
          signupToken={token}
          signupCode={code}
        />
      )}
    </SoftwallContext.Provider>
  );
}

export function useSoftwall() {
  return useContext(SoftwallContext);
}
